import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { printFilledForm } from "@/lib/printForm";
import logo from "@/assets/galahadji-logo.png";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileSignature, ClipboardList, Printer, Save, Pencil, Copy, Eye, Layers, Wand2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const CAT = { contract: "Contracts & Agreements", internal: "Internal Forms" };

const prefillFromLead = (L, companyName) => {
  const o = L?.owner || {}, p = L?.property || {};
  const addr = [p.property_address, p.city, p.state, p.zip_code].filter(Boolean).join(", ");
  const ownerName = [o.first_name, o.last_name].filter(Boolean).join(" ");
  const map = {
    seller_name: ownerName, grantor_name: ownerName,
    seller_address: o.mailing_address, seller_email: o.email, seller_phone: o.phone,
    property_address: addr, collateral_property: addr,
    purchase_price: L?.asking_price, asking_price: L?.asking_price,
    mortgage_balance: L?.existing_loan_balance, approx_balance: L?.existing_loan_balance,
    county: p.county, state: p.state,
    buyer_name: companyName, grantee_name: companyName, assignor_name: companyName,
  };
  return Object.fromEntries(Object.entries(map).filter(([, v]) => v !== undefined && v !== null && v !== ""));
};

export default function Forms() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [packets, setPackets] = useState([]);
  const [leads, setLeads] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [filling, setFilling] = useState(null);
  const [editing, setEditing] = useState(null);
  const [params] = useSearchParams();
  const presetLeadId = params.get("lead_id") || "";
  const isAdmin = ["System Owner", "Administrator"].includes(user?.role);

  const load = () => api.get("/forms/templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/forms/packets").then((r) => setPackets(r.data)).catch(() => {});
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/company").then((r) => setCompanyName(r.data?.name || "")).catch(() => {});
  }, []);

  const duplicate = async (t) => {
    try {
      await api.post("/forms/templates", { name: `${t.name} (Copy)`, category: t.category, description: t.description, fields: t.fields, body_html: t.body_html, logo_url: t.logo_url });
      toast.success("Template duplicated — customize your copy");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const grid = (cat) => {
    const items = templates.filter((t) => t.category === cat && t.active !== false);
    if (items.length === 0) return <div className="text-muted-foreground bg-card border border-border rounded-md p-10 text-center">No templates yet.</div>;
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t) => (
          <div key={t.id} className="bg-card border border-border rounded-md p-5 flex flex-col hover:-translate-y-1 hover:shadow-sm transition-transform duration-200" data-testid={`form-template-${t.id}`}>
            <div className="flex items-start justify-between">
              {cat === "contract" ? <FileSignature className="w-8 h-8 text-accent" /> : <ClipboardList className="w-8 h-8 text-accent" />}
              {t.built_in && <Badge variant="outline" className="text-[10px]">Standard</Badge>}
            </div>
            <div className="font-display font-bold mt-3">{t.name}</div>
            <p className="text-xs text-muted-foreground mt-1 flex-1">{t.description}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button size="sm" onClick={() => setFilling({ kind: "template", item: t })} data-testid={`fill-form-${t.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Fill Out</Button>
              <Button size="sm" variant="outline" onClick={() => setEditing({ ...t })} data-testid={`customize-form-${t.id}`}>Customize</Button>
              <Button size="sm" variant="ghost" onClick={() => duplicate(t)} title="Duplicate" data-testid={`duplicate-form-${t.id}`}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <PageHeader title="Forms & Documents" subtitle="Fill, print, e-sign, and save contract and internal forms — your logo goes top-left on every document" />
      <div className="p-4 sm:p-6 lg:p-8">
        {packets.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 font-display font-bold text-lg"><Layers className="w-5 h-5 text-accent" /> One-Click Packets</div>
            <p className="text-xs text-muted-foreground mt-1">Fill once — every document in the packet is generated and printed together.</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {packets.map((p) => (
                <div key={p.id} className="bg-primary text-primary-foreground border border-border rounded-md p-5" data-testid={`packet-${p.id}`}>
                  <div className="font-display font-bold">{p.name}</div>
                  <p className="text-xs text-white/70 mt-1">{p.description}</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">{p.templates.map((t) => <Badge key={t.id} className="bg-accent/20 text-accent border-0 text-[10px]">{t.name}</Badge>)}</div>
                  <Button size="sm" className="mt-4 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setFilling({ kind: "packet", item: p })} data-testid={`fill-packet-${p.id}`}><Wand2 className="w-3.5 h-3.5 mr-1" /> Fill Packet</Button>
                </div>
              ))}
            </div>
          </div>
        )}
        <Tabs defaultValue="contract">
          <TabsList>
            <TabsTrigger value="contract" data-testid="tab-contract-forms">{CAT.contract}</TabsTrigger>
            <TabsTrigger value="internal" data-testid="tab-internal-forms">{CAT.internal}</TabsTrigger>
          </TabsList>
          <TabsContent value="contract" className="mt-5">{grid("contract")}</TabsContent>
          <TabsContent value="internal" className="mt-5">{grid("internal")}</TabsContent>
        </Tabs>
        <p className="text-xs text-muted-foreground mt-8 max-w-2xl">Contract templates are starting points for negotiation — always have final agreements reviewed by a licensed attorney in the property state. Use <strong>Customize</strong> to adjust wording or replace the logo, or <strong>Duplicate</strong> to keep the original intact.</p>
      </div>
      {filling && <FillDialog target={filling} leads={leads} companyName={companyName} presetLeadId={presetLeadId} onClose={() => setFilling(null)} />}
      {editing && <EditDialog template={editing} isAdmin={isAdmin} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function FillDialog({ target, leads, companyName, presetLeadId, onClose }) {
  const isPacket = target.kind === "packet";
  const item = target.item;
  const [values, setValues] = useState({});
  const [leadId, setLeadId] = useState(presetLeadId);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setValues((p) => ({ ...p, [k]: v }));

  const applyLead = async (id) => {
    setLeadId(id);
    if (!id) return;
    try {
      const { data } = await api.get(`/leads/${id}`);
      const pre = prefillFromLead(data, companyName);
      const keys = new Set(item.fields.map((f) => f.key));
      setValues((prev) => {
        const next = { ...prev };
        Object.entries(pre).forEach(([k, v]) => { if (keys.has(k) && !next[k]) next[k] = String(v); });
        return next;
      });
      toast.success("Pre-filled from the lead — review every field before printing");
    } catch { /* lead fetch failed silently */ }
  };

  useEffect(() => { if (presetLeadId) applyLead(presetLeadId); }, []); // eslint-disable-line

  const render = async (save) => {
    setBusy(true);
    try {
      const payload = isPacket
        ? { packet_id: item.id, values, lead_id: save ? leadId : undefined, save }
        : { template_id: item.id, values, lead_id: save ? leadId : undefined, save };
      const { data } = await api.post(isPacket ? "/forms/fill-packet" : "/forms/fill", payload);
      setPreview(data);
      if (save) toast.success("Saved to the lead's Documents");
      return data;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doPrint = async () => {
    const data = preview || (await render(false));
    if (data) printFilledForm(item.name, data.html, data.logo_url);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{item.name}</DialogTitle>
          <DialogDescription>{isPacket ? "Fill once — all documents in the packet are generated together." : "Fill in the fields, preview the document, then print or save it to a lead."}</DialogDescription></DialogHeader>
        {!preview ? (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Link to Lead — auto-fills seller & property fields</Label>
              <Select value={leadId} onValueChange={applyLead}>
                <SelectTrigger data-testid="form-lead-select"><SelectValue placeholder="Optional — choose a lead to pre-fill" /></SelectTrigger>
                <SelectContent>{leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.lead_id_display} · {l.property?.property_address || "No address"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {item.fields.map((f) => (
              <div key={f.key} className={`space-y-1.5 ${f.type === "textarea" ? "sm:col-span-2" : ""}`}>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{f.label}</Label>
                {f.type === "textarea"
                  ? <Textarea rows={3} value={values[f.key] || ""} onChange={(e) => set(f.key)(e.target.value)} data-testid={`form-field-${f.key}`} />
                  : <Input type={f.type === "number" ? "number" : f.type} value={values[f.key] || ""} onChange={(e) => set(f.key)(e.target.value)} data-testid={`form-field-${f.key}`} />}
              </div>
            ))}
          </div>
        ) : (
          <div className="border border-border rounded-md bg-white text-black p-6 overflow-x-auto" data-testid="form-preview">
            <div className="flex items-center gap-4 border-b-4 border-[#d4a017] pb-3 mb-5">
              <img src={preview.logo_url || logo} alt="logo" className="h-14 w-auto object-contain" />
              <div><div className="font-bold text-lg" style={{ fontFamily: "Arial" }}>Galahadji Acquisitions Inc</div>
                <div className="text-xs text-gray-500">Las Vegas, NV · Acquire · Transform · Build Wealth</div></div>
            </div>
            <div style={{ fontFamily: "Georgia, serif", lineHeight: 1.55 }} dangerouslySetInnerHTML={{ __html: preview.html }} />
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2">
          {preview && <Button variant="outline" onClick={() => setPreview(null)} data-testid="form-back-btn">Back to edit</Button>}
          {!preview && <Button variant="outline" onClick={() => render(false)} disabled={busy} data-testid="form-preview-btn"><Eye className="w-4 h-4 mr-1" /> Preview</Button>}
          <Button onClick={doPrint} disabled={busy} data-testid="form-print-btn"><Printer className="w-4 h-4 mr-1" /> Print / Save as PDF</Button>
          <Button onClick={() => render(true)} disabled={busy || !leadId} title={!leadId ? "Choose a lead first" : ""} data-testid="form-save-btn"><Save className="w-4 h-4 mr-1" /> {busy ? "Saving…" : "Save to Lead"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ template, isAdmin, onClose, onSaved }) {
  const [t, setT] = useState(template);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setT((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/forms/templates/${t.id}`, { name: t.name, description: t.description, body_html: t.body_html, logo_url: t.logo_url });
      toast.success("Template updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${t.name}" from the gallery?`)) return;
    try { await api.delete(`/forms/templates/${t.id}`); toast.success("Template removed"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Customize — {template.name}</DialogTitle>
          <DialogDescription>Adjust the wording, name, or logo. Keep the {"{{placeholders}}"} so the form stays fillable.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Template Name</Label>
            <Input value={t.name} onChange={(e) => set("name")(e.target.value)} data-testid="edit-form-name" /></div>
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description</Label>
            <Input value={t.description || ""} onChange={(e) => set("description")(e.target.value)} data-testid="edit-form-desc" /></div>
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Custom Logo URL (leave blank to use the Galahadji logo)</Label>
            <Input value={t.logo_url || ""} onChange={(e) => set("logo_url")(e.target.value)} placeholder="https://…/my-logo.png" data-testid="edit-form-logo" /></div>
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Document Body (HTML — keep the {"{{placeholders}}"} to stay fillable)</Label>
            <Textarea rows={14} className="font-mono text-xs" value={t.body_html || ""} onChange={(e) => set("body_html")(e.target.value)} data-testid="edit-form-body" /></div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {isAdmin && <Button variant="outline" className="text-destructive border-destructive/30 mr-auto" onClick={remove} data-testid="edit-form-delete">Remove</Button>}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="edit-form-save">{busy ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
